import { Link } from "react-router-dom";
import { Check, Star } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { Stagger, StaggerItem } from "@/components/motion/Reveal";
import { PLANS } from "@/data/content";

export const PricingSection = ({ compact = false }) => (
  <section className="py-20 lg:py-28 bg-brand-bg" data-testid="pricing-section">
    <div className="container-x">
      <SectionHeading
        align="center"
        eyebrow="Monitoring Plans"
        title={<>Simple pricing. <span className="text-brand-red">No surprises.</span></>}
        sub="Every plan includes 24/7 US-based monitoring and can be cancelled any time. No activation fees, ever."
      />

      <Stagger className="mt-14 grid gap-6 lg:grid-cols-3 items-stretch" gap={0.12}>
        {PLANS.map((p) => (
          <StaggerItem key={p.name} className={p.popular ? "lg:-mt-4 lg:mb-4" : ""}>
            <div
              data-testid={`plan-${p.name.toLowerCase()}`}
              className={`relative h-full flex flex-col rounded-3xl p-8 border transition-all duration-300 ${
                p.popular
                  ? "bg-brand-navy border-brand-navy text-white shadow-cardHover"
                  : "bg-white border-brand-line shadow-card"
              }`}
            >
              {p.popular && (
                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-brand-red text-white text-xs font-bold uppercase tracking-wide">
                  <Star className="w-3.5 h-3.5 fill-white" /> Most Popular
                </span>
              )}
              <h3 className={`font-display font-bold text-2xl ${p.popular ? "text-white" : "text-brand-navy"}`}>
                {p.name}
              </h3>
              <p className={`mt-1 ${p.popular ? "text-white/70" : "text-brand-ink2"}`}>{p.tagline}</p>
              <div className="mt-6 flex items-end gap-1">
                <span className={`font-display font-black text-5xl ${p.popular ? "text-white" : "text-brand-navy"}`}>
                  ${p.price}
                </span>
                <span className={`mb-2 font-semibold ${p.popular ? "text-white/60" : "text-brand-ink2"}`}>
                  /month
                </span>
              </div>

              <ul className="mt-7 space-y-3.5 flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span
                      className={`grid place-items-center w-5 h-5 rounded-full shrink-0 mt-0.5 ${
                        p.popular ? "bg-brand-red" : "bg-brand-red/10"
                      }`}
                    >
                      <Check className={`w-3.5 h-3.5 ${p.popular ? "text-white" : "text-brand-red"}`} strokeWidth={3} />
                    </span>
                    <span className={p.popular ? "text-white/90" : "text-brand-navy"}>{f}</span>
                  </li>
                ))}
              </ul>

              <Link
                to={`/contact?plan=${p.name}`}
                data-testid={`plan-cta-${p.name.toLowerCase()}`}
                className={`btn-focus mt-8 inline-flex items-center justify-center h-14 rounded-full font-bold text-lg transition-colors ${
                  p.popular
                    ? "bg-brand-red text-white hover:bg-brand-red-600"
                    : "bg-brand-navy text-white hover:bg-brand-navy-700"
                }`}
              >
                Choose {p.name}
              </Link>
            </div>
          </StaggerItem>
        ))}
      </Stagger>

      {!compact && (
        <p className="mt-8 text-center text-brand-ink2">
          Not sure which plan fits?{" "}
          <Link to="/contact" className="font-bold text-brand-red link-underline">
            Talk to a specialist
          </Link>{" "}
          — free, no pressure.
        </p>
      )}
    </div>
  </section>
);
